// If you want to use Phoenix channels, run `mix help phx.gen.channel`
// to get started and then uncomment the line below.
// import "./user_socket.js"

// You can include dependencies in two ways.
//
// The simplest option is to put them in assets/vendor and
// import them using relative paths:
//
//     import "../vendor/some-package.js"
//
// Alternatively, you can `npm install some-package --prefix assets` and import
// them using a path starting with the package name:
//
//     import "some-package"
//

// Include phoenix_html to handle method=PUT/DELETE in forms and buttons.
import "phoenix_html";
// Establish Phoenix Socket and LiveView configuration.
import { LongPoll, Socket } from "phoenix";
import { LiveSocket } from "phoenix_live_view";
import { hooks as colocatedHooks } from "phoenix-colocated/zulti";
import topbar from "../vendor/topbar";
// Named imports (not `import * as`) so esbuild can tree-shake unused Sentry
// integrations such as replay and feedback out of the initial bundle.
import {
  addIntegration as sentryAddIntegration,
  captureException as sentryCaptureException,
  init as sentryInit,
  setUser as sentrySetUser,
} from "@sentry/browser";

import LocalStorageHook from "./hooks/localStorageHook";
import SpinFtpWheelHook from "./hooks/spinFtpWheelHook";
import AutoClearFlashHook from "./hooks/autoClearFlashHook";
import AnimatedCounterHook from "./hooks/animatedCounterHook";
import TopWinnersCarouselHook from "./hooks/topWinnersCarouselHook";
import ScrollCompensateHook from "./hooks/scrollCompensateHook";
import LeaderboardRowsHook from "./hooks/leaderboardRowsHook";
import {
  ensureStoredSoundEnabled,
  registerGameSoundEvents,
} from "./sound_manager";
import ZSelectHook from "./hooks/zSelectHook";
import { registerGlobalHaptics } from "./haptics";
import { isChunkLoadError } from "./chunk_loader";
import { safeStorage } from "./storage";

registerGameSoundEvents();
ensureStoredSoundEnabled();
// One delegated listener drives tap haptics for every button and link.
registerGlobalHaptics();

const HOOK_LIFECYCLE_CALLBACKS = [
  "mounted",
  "beforeUpdate",
  "updated",
  "destroyed",
  "disconnected",
  "reconnected",
];

function lazyHook(load) {
  let hookPromise;
  let realHook;

  const replayQueuedCallbacks = (context, loadedHook) => {
    if (context.__lazyHookReady) return loadedHook;

    context.__lazyHookReady = true;
    Object.assign(context, loadedHook);

    for (const [callback, args] of context.__lazyHookQueue || []) {
      const handler = loadedHook[callback];
      if (typeof handler === "function") {
        handler.apply(context, args);
      }
    }

    context.__lazyHookQueue = [];
    return loadedHook;
  };

  const loadHook = (context) => {
    hookPromise =
      hookPromise ||
      Promise.resolve()
        .then(load)
        .then((loaded) => {
          realHook = loaded?.default || loaded;
          return realHook;
        });
    return hookPromise
      .then((loadedHook) =>
        context ? replayQueuedCallbacks(context, loadedHook) : loadedHook,
      )
      .catch((error) => {
        // Let a later lifecycle callback retry the import.
        if (!realHook) hookPromise = null;
        if (isChunkLoadError(error)) {
          console.warn("Failed to load hook chunk", error);
        } else {
          sentryCaptureException(error, { tags: { hook: "lazyHook" } });
        }
      });
  };

  const attachHook = (context) => {
    if (!realHook) return null;
    Object.assign(context, realHook);
    context.__lazyHookReady = true;
    return realHook;
  };

  return Object.fromEntries(
    HOOK_LIFECYCLE_CALLBACKS.map((callback) => [
      callback,
      function (...args) {
        if (realHook) {
          const loadedHook = attachHook(this);
          const handler = loadedHook?.[callback];
          if (typeof handler === "function") {
            return handler.apply(this, args);
          }
          return;
        }

        this.__lazyHookQueue = this.__lazyHookQueue || [];
        this.__lazyHookQueue.push([callback, args]);

        if (callback === "beforeUpdate") {
          void loadHook(this);
          return;
        }

        void loadHook(this);
      },
    ]),
  );
}

const environment = document
  .querySelector("meta[name='sentry-environment']")
  ?.getAttribute("content");

// LiveView method names survive minification, so this also matches prod frames.
const isLiveViewRenderFrame = (frame) =>
  frame.function?.includes("dynamicsToBuffer");

// Mirrors ZultiWeb.Plugs.SetUserContext on the server so client-side errors are
// attributed to the same user.
const sentryUser = (() => {
  const id = document
    .querySelector("meta[name='sentry-user-id']")
    ?.getAttribute("content");

  if (!id) return null;

  const username = document
    .querySelector("meta[name='sentry-username']")
    ?.getAttribute("content");

  return username ? { id, username } : { id };
})();

if (environment === "prod") {
  sentryInit({
    dsn: "https://67a3eb914da34b99238867eacc5b8950@o4509000790441984.ingest.us.sentry.io/4509000791031808",
    environment: environment,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    ignoreErrors: ["play()"],
    denyUrls: [/extensions\//i, /^chrome-extension:\/\//i],
    beforeSend(event) {
      // Drop the error if the user's browser lost internet connection entirely
      if (typeof navigator !== "undefined" && !navigator.onLine) {
        return null;
      }

      const message =
        event.message ||
        (event.exception &&
          event.exception.values &&
          event.exception.values[0] &&
          event.exception.values[0].value);

      if (message) {
        if (
          // https://tree3-hp.sentry.io/issues/7166096428/
          message.match(/Method not found/i) ||
          // https://tree3-hp.sentry.io/issues/7217427975/
          message.match(/browser is not defined/i) ||
          // https://tree3-hp.sentry.io/issues/7515081966/
          message.match(/cids_will_destroy/i) ||
          // https://tree3-hp.sentry.io/issues/7327661868/
          // Script injected by the Twitter in-app browser.
          message.match(/Can't find variable: CONFIG/i)
        ) {
          return null;
        }

        if (
          Math.random() > 0.01 &&
          // https://tree3-hp.sentry.io/issues/7165799744/
          (message.match(/The operation is insecure/i) ||
            // https://tree3-hp.sentry.io/issues/7666732298/
            message.match(/push timeout/i) ||
            // https://tree3-hp.sentry.io/issues/7135989393/
            message.match(/undefined is not an object/i) ||
            // https://tree3-hp.sentry.io/issues/7209636419/
            message.match(/Java object is gone/i) ||
            // https://tree3-hp.sentry.io/issues/7487443119/
            message.match(/AbortError: The operation was aborted/i) ||
            // https://tree3-hp.sentry.io/issues/7165799744/
            message.match(/The operation is insecure/i) ||
            // https://tree3-hp.sentry.io/issues/7485418866/
            message.match(
              /not allowed by the user agent or the platform in the current context/i,
            ) ||
            // https://tree3-hp.sentry.io/issues/7165745323/
            message === "timeout")
        ) {
          return null;
        }
      }

      if (event.location && event.location.match(/chrome-extension/i)) {
        return null;
      }

      const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
      const isExtensionError = frames.some(
        (f) =>
          f.filename?.startsWith("chrome-extension://") ||
          f.filename?.startsWith("moz-extension://"),
      );

      // https://tree3-hp.sentry.io/issues/7130700618/
      // Recursion in Google code injected by Chrome iOS / the Google app (`_.Fi`, `<anonymous>`).
      const isInjectedStackOverflow =
        message?.match(/Maximum call stack size exceeded/i) &&
        !frames.some((f) => f.filename?.includes("/assets/"));

      // https://tree3-hp.sentry.io/issues/7135989393/
      // Form autofill script injected by some Android browsers (`Form.getValuesReadyForStorage`).
      // Our bundle never runs as `<anonymous>`, so an error thrown there isn't ours.
      const isInjectedScriptError =
        frames.length > 0 && frames[frames.length - 1].filename === "<anonymous>";

      // https://tree3-hp.sentry.io/issues/7666775445/
      // Recovered from by reconnecting the LiveSocket (see below), so only keep a sample.
      const isLiveViewDesync =
        Math.random() > 0.01 && frames.some(isLiveViewRenderFrame);

      return isExtensionError ||
        isInjectedStackOverflow ||
        isInjectedScriptError ||
        isLiveViewDesync
        ? null
        : event;
    },
  });

  if (sentryUser) {
    sentrySetUser(sentryUser);
  }

  // Session replay is only used for errors (replaysOnErrorSampleRate), so it is
  // split into its own chunk and added once the page is idle rather than
  // weighing down the initial bundle.
  const loadReplay = () =>
    Promise.all([
      import("@sentry-internal/replay"),
      import("@sentry-internal/replay-canvas"),
    ])
      .then(([{ replayIntegration }, { replayCanvasIntegration }]) => {
        sentryAddIntegration(
          replayIntegration({ maskAllText: true, blockAllMedia: true }),
        );
        sentryAddIntegration(replayCanvasIntegration());
      })
      .catch(() => {});

  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(loadReplay, { timeout: 5000 });
  } else {
    window.addEventListener("load", () => setTimeout(loadReplay, 0), {
      once: true,
    });
  }
}

let Hooks = {};
Hooks.Turnstile = lazyHook(() =>
  import("phoenix_turnstile").then(({ TurnstileHook }) => TurnstileHook),
);
Hooks.LivePhone = lazyHook(() => import("live_phone"));
Hooks.LocalStorageHook = LocalStorageHook;
Hooks.SpinFtpWheel = SpinFtpWheelHook;
Hooks.RewardReel = lazyHook(() => import("./hooks/rewardReelHook"));
Hooks.ReferralReels = lazyHook(() => import("./hooks/referralReelsHook"));
Hooks.AutoClearFlashHook = AutoClearFlashHook;
Hooks.ScratchCardHook = lazyHook(() => import("./hooks/scratchCardHook"));
Hooks.TicketRevealHook = lazyHook(() => import("./hooks/ticketRevealHook"));
Hooks.WakeLockHook = lazyHook(() => import("./hooks/wakeLockHook"));
Hooks.SoundToggleButton = lazyHook(() => import("./hooks/soundToggleHook"));
Hooks.AnimatedCounter = AnimatedCounterHook;
Hooks.TopWinnersCarousel = TopWinnersCarouselHook;
Hooks.Countdown = lazyHook(() => import("./hooks/countdownHook"));
Hooks.Zlinko = lazyHook(() => import("./hooks/zlinko"));
Hooks.ZlotsHook = lazyHook(() => import("./hooks/zlotsHook"));
Hooks.TooltipHook = lazyHook(() => import("./hooks/tooltipHook"));
Hooks.BlockedActionHook = lazyHook(() => import("./hooks/blockedActionHook"));
Hooks.SocialAppLinkHook = lazyHook(() => import("./hooks/socialAppLinkHook"));
Hooks.LocalDateTime = lazyHook(() => import("./hooks/localDateTimeHook"));
Hooks.WheelPicker = lazyHook(() => import("./hooks/wheelPickerHook"));
Hooks.ScrollCompensate = ScrollCompensateHook;
Hooks.LevelBarHook = lazyHook(() => import("./hooks/levelBarHook"));
Hooks.EnterTicketHook = lazyHook(() => import("./hooks/enterTicketHook"));
Hooks.DraftTicketHook = lazyHook(() => import("./hooks/draftTicketHook"));
Hooks.LevelPackAnimation = lazyHook(() => import("./hooks/levelPackHook"));
Hooks.FlipCards = lazyHook(() => import("./hooks/flipCardsHook"));
Hooks.BirthdayPickerHook = lazyHook(() => import("./hooks/birthdayPickerHook"));
Hooks.PhoneCodeInput = lazyHook(() => import("./hooks/phoneCodeInputHook"));
Hooks.JumpingText = lazyHook(() => import("./hooks/jumpingTextHook"));
Hooks.MascotTypewriter = lazyHook(() => import("./hooks/mascotTypewriterHook"));
Hooks.MascotVideo = lazyHook(() => import("./hooks/mascotVideoHook"));
Hooks.EnterNextSubStep = lazyHook(() => import("./hooks/enterNextSubStepHook"));
Hooks.ModalScroll = lazyHook(() => import("./hooks/modalScrollHook"));
Hooks.ConfirmAutoSubmitHook = lazyHook(
  () => import("./hooks/confirmAutoSubmitHook"),
);
Hooks.MagicLinkAutoSubmitHook = lazyHook(
  () => import("./hooks/magicLinkAutoSubmitHook"),
);
Hooks.AnalyticsTimezone = lazyHook(
  () => import("./hooks/analyticsTimezoneHook"),
);
Hooks.ListFilter = lazyHook(() => import("./hooks/listFilterHook"));
Hooks.MapHover = lazyHook(() => import("./hooks/mapHoverHook"));
Hooks.SortableGrid = lazyHook(() => import("./hooks/sortableGridHook"));
Hooks.ZSelect = ZSelectHook;
Hooks.RiveAnimationHook = lazyHook(() => import("./hooks/riveAnimationHook"));
Hooks.LeaderboardPodium = lazyHook(
  () => import("./hooks/leaderboardPodiumHook"),
);
Hooks.LeaderboardRows = LeaderboardRowsHook;
Hooks.LeaderboardCountdown = lazyHook(
  () => import("./hooks/leaderboardCountdownHook"),
);
Hooks.LiveDraw = lazyHook(() =>
  import("./hooks/liveDrawHook").then(({ LiveDrawHook }) => LiveDrawHook),
);
Hooks.LiveDrawCountdown = lazyHook(() =>
  import("./hooks/liveDrawHook").then(
    ({ LiveDrawCountdownHook }) => LiveDrawCountdownHook,
  ),
);
Hooks.LiveDrawJoiners = lazyHook(() =>
  import("./hooks/liveDrawHook").then(
    ({ LiveDrawJoinersHook }) => LiveDrawJoinersHook,
  ),
);

Hooks.DropBallHook = {
  async mounted() {
    this.el.addEventListener("click", () => {
      this.pushEventTo(`#${this.el.id}`, "drop", {});
    });
  },
};

Hooks.DetectRegion = {
  async mounted() {
    try {
      // 1. Timezone lookup via npm package
      let country = await this.getCountryFromTimezone();

      // 2. Locale string (e.g., "en-AU" -> "AU")
      if (!country) {
        country = this.getCountryFromLocale();
      }

      if (country) {
        this.pushEventTo(`#${this.el.id}`, "set-region", { region: country });
        return;
      }

      // 3. IP API
      const res = await fetch("https://ipapi.co/json/");
      const data = await res.json();
      if (data && data.country_code) {
        this.pushEventTo(`#${this.el.id}`, "set-region", {
          region: data.country_code,
        });
      }
    } catch (e) {
      sentryCaptureException(e, { tags: { hook: "DetectRegion" } });
    }
  },

  async getCountryFromTimezone() {
    try {
      const { getCountryForTimezone } = await import("countries-and-timezones");
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (!timeZone) return null;

      // Returns object like: { id: "AU", name: "Australia", ... }
      const country = getCountryForTimezone(timeZone);
      return country ? country.id : null;
    } catch (e) {
      return null;
    }
  },

  getCountryFromLocale() {
    const locale =
      navigator.language || (navigator.languages && navigator.languages[0]);
    if (locale && locale.includes("-")) {
      const code = locale.split("-")[1].toUpperCase();
      return code.length === 2 ? code : null;
    }
    return null;
  },
};

// Hook to auto-scroll to a specific element on the page
Hooks.ScrollToHook = {
  mounted() {
    this.handleEvent("scroll_to_bottom", () => {
      setTimeout(() => {
        const bottomElement = document.getElementById(
          this.el.getAttribute("scroll-to-id"),
        );
        bottomElement?.scrollIntoView({ behavior: "smooth", block: "end" });
      }, 200);
    });
  },
};

// Hook to handle Cookiebot declarations
Hooks.CookieDeclaration = {
  mounted() {
    // Initialize the cookie declaration
    this.initCookieDeclaration();

    // Keep reference to the observer
    this.observer = new MutationObserver(() => {
      // When DOM changes in the container, check if we need to reinitialize
      if (!document.getElementById("CookieDeclaration")) {
        this.initCookieDeclaration();
      }
    });

    // Observe changes to this element
    this.observer.observe(this.el, { childList: true });
  },

  updated() {
    // Check if the declaration exists, if not reinitialize
    if (!document.getElementById("CookieDeclaration")) {
      this.initCookieDeclaration();
    }
  },

  destroyed() {
    // Clean up the observer when component is destroyed
    if (this.observer) {
      this.observer.disconnect();
    }
  },

  initCookieDeclaration() {
    try {
      // Remove any existing script to avoid duplicates
      const oldScript = document.getElementById("CookieDeclaration");
      if (oldScript) {
        oldScript.remove();
      }

      // Create and add a new script
      const script = document.createElement("script");
      script.id = "CookieDeclaration";
      script.src =
        "https://consent.cookiebot.com/8d0503c6-cc06-4c8b-8cac-fe2c2946c7c3/cd.js";
      script.type = "text/javascript";
      script.async = true;

      // Get nonce from the container element or fallback to the meta tag
      const nonce =
        this.el.getAttribute("data-nonce") ||
        document
          .querySelector("meta[name='csp-nonce']")
          ?.getAttribute("content");
      if (nonce) {
        script.setAttribute("nonce", nonce);
      }

      // Add the new script to the container
      this.el.appendChild(script);
    } catch (e) {
      console.error("Error initializing cookie declaration:", e);
    }
  },
};

// Charts (by id) whose legend should isolate the clicked series (deselecting the
// rest) on a plain click, while a shift+click toggles just the clicked series
// on/off (leaving the rest untouched).
const ISOLATE_LEGEND_CHART_IDS = ["daily-users-by-country-chart"];

// ApexCharts' legendClick callback doesn't expose the originating DOM event, so
// we can't read `shiftKey` from it directly. Capture it from the preceding
// mousedown (capture phase, so it always runs before ApexCharts' own handler).
let legendShiftKeyHeld = false;
if (typeof document !== "undefined") {
  document.addEventListener(
    "mousedown",
    (event) => {
      legendShiftKeyHeld = event.shiftKey === true;
    },
    true,
  );
}

function applyLegendIsolateBehaviour(config) {
  const chartId = config && config.chart && config.chart.id;
  if (!chartId || !ISOLATE_LEGEND_CHART_IDS.includes(chartId)) return;

  config.chart.events = config.chart.events || {};
  config.chart.events.legendClick = (chartContext, seriesIndex) => {
    const globals = chartContext.w.globals;
    const seriesNames = globals.seriesNames || [];
    const total = seriesNames.length;
    if (total === 0 || seriesIndex == null) return;

    const collapsed = globals.collapsedSeriesIndices || [];
    const clickedName = seriesNames[seriesIndex];
    const clickedHidden = collapsed.includes(seriesIndex);

    // Each showSeries/hideSeries call triggers a full (animated) chart
    // re-render, so applying it to every series is what made large selections
    // sluggish. Only touch series whose visibility actually needs to change so
    // that repeated clicks (e.g. jumping between single countries) cost ~2
    // redraws instead of one per country.
    const setVisibility = (name, index, shouldBeVisible) => {
      const isHidden = collapsed.includes(index);
      if (shouldBeVisible && isHidden) {
        chartContext.showSeries(name);
      } else if (!shouldBeVisible && !isHidden) {
        chartContext.hideSeries(name);
      }
    };

    if (legendShiftKeyHeld) {
      // Shift + click → toggle just the clicked country on/off, leaving the
      // rest of the selection as-is.
      setVisibility(clickedName, seriesIndex, clickedHidden);
    } else {
      const visibleCount = total - collapsed.length;

      if (!clickedHidden && visibleCount === 1) {
        // Clicking the only visible country → show all countries again.
        // `resetSeries` restores every series in a single re-render instead of
        // one per country.
        chartContext.resetSeries();
      } else {
        // Plain click → isolate the clicked country (show only it), regardless of
        // whether it was previously toggled off.
        seriesNames.forEach((name, i) =>
          setVisibility(name, i, i === seriesIndex),
        );
      }
    }
  };
}

// Investor dashboard charts (by id) whose values are percentages. LiveCharts
// serialises its config to JSON, so these formatters can't come from Elixir.
// Kept in step with `Charts.percent_axis_chart_ids/0`.
const PERCENT_AXIS_CHART_IDS = [
  "investor-retention-chart",
  "investor-conversion-chart",
];

function applyPercentFormatters(config) {
  const chartId = config && config.chart && config.chart.id;
  if (!chartId || !PERCENT_AXIS_CHART_IDS.includes(chartId)) return;

  applyValueFormatter(config, (value) =>
    value == null || isNaN(value) ? value : `${Number(value).toFixed(1)}%`,
  );
}

// Investor dashboard charts (by id) whose values are US dollars.
// Kept in step with `Charts.usd_axis_chart_ids/0`.
const USD_AXIS_CHART_IDS = ["investor-user-payouts-chart"];

// Older engines (e.g. VivoBrowser) throw a RangeError when max < the currency's
// default minimum of 2, so both are set explicitly.
const usdFormat = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

function applyUsdFormatters(config) {
  const chartId = config && config.chart && config.chart.id;
  if (!chartId || !USD_AXIS_CHART_IDS.includes(chartId)) return;

  applyValueFormatter(config, (value) =>
    value == null || isNaN(value) ? value : usdFormat.format(value),
  );
}

function applyValueFormatter(config, format) {
  const axes = Array.isArray(config.yaxis)
    ? config.yaxis
    : [config.yaxis || {}];
  axes.forEach((axis) => {
    axis.labels = axis.labels || {};
    axis.labels.formatter = format;
  });
  config.yaxis = Array.isArray(config.yaxis) ? axes : axes[0];

  config.tooltip = config.tooltip || {};
  config.tooltip.y = { ...(config.tooltip.y || {}), formatter: format };
}

let csrfToken = document
  .querySelector("meta[name='csrf-token']")
  .getAttribute("content");
let cspNonce = document
  .querySelector("meta[name='csp-nonce']")
  .getAttribute("content");

// While a pod is replaced the ingress answers polls with a JSON `status` of 502-504, which
// phoenix.js throws on ("unhandled poll status"), leaving the LiveView stuck until the
// heartbeat times out. Treat them like an unreachable server, which closes and retries.
const longPollAjax = LongPoll.prototype.ajax;
LongPoll.prototype.ajax = function (method, headers, body, onCallerTimeout, callback) {
  return longPollAjax.call(this, method, headers, body, onCallerTimeout, (resp) =>
    callback(resp?.status > 500 ? null : resp),
  );
};

let liveSocket = new LiveSocket("/live", Socket, {
  longPollFallbackMs: 5000,
  // Full jitter: when the ingress drops every socket at once, they must not reconnect in lockstep
  reconnectAfterMs: (tries) => Math.random() * Math.min(1000 * 2 ** (tries - 1), 10000),
  sessionStorage: safeStorage("sessionStorage"),
  localStorage: safeStorage("localStorage"),
  params: { _csrf_token: csrfToken, _csp_nonce: cspNonce },
  hooks: {
    // Your custom hooks
    ...Hooks,
    ...colocatedHooks,
    // Lazy-load LiveCharts hooks: dynamically import on first mount
    "LiveCharts.Hooks.ApexCharts": {
      async mounted() {
        let LiveCharts;
        try {
          LiveCharts = (await import("live_charts")).default;
        } catch (error) {
          if (isChunkLoadError(error)) return;
          throw error;
        }
        const realHook = LiveCharts.Hooks["LiveCharts.Hooks.ApexCharts"];
        Object.assign(this, realHook);
        // Inject custom legend behaviour (isolate-on-click) for opted-in charts.
        const originalGetConfig = this.getConfig.bind(this);
        this.getConfig = () => {
          const config = originalGetConfig();
          applyLegendIsolateBehaviour(config);
          applyPercentFormatters(config);
          applyUsdFormatters(config);
          return config;
        };
        if (realHook.mounted) realHook.mounted.call(this);
      },
    },
  },
});

// Show progress bar on live navigation and form submits
topbar.config({
  barColors: { 0: "#9B2DF5" },
  shadowColor: "rgba(0, 0, 0, .3)",
});
window.addEventListener("phx:page-loading-start", (_info) => topbar.show(300));
window.addEventListener("phx:page-loading-stop", (_info) => topbar.hide());

// Handle LiveView navigation errors (e.g. 404, 500) by reloading the page.
// This triggers a full HTTP request which either renders the page or
// falls through to the catch-all route that redirects to /.
window.addEventListener("phx:page-loading-start", (event) => {
  if (event.detail.kind === "error") {
    const targetHref = event.detail.to;
    // Delay briefly to avoid rapid loops
    setTimeout(() => {
      if (targetHref) {
        window.location.href = targetHref;
      } else {
        window.location.reload();
      }
    }, 2000);
  }
});
window.addEventListener("phx:copy", (event) => {
  const btn = event.target;
  const text = event.detail.content || btn.textContent?.trim();

  if (!text) {
    console.warn("No text to copy!");
    return;
  }
  if (!btn.dataset.originalHtml) {
    btn.dataset.originalHtml = btn.innerHTML;
  }
  if (btn.dataset.resetTimeoutId) {
    clearTimeout(btn.dataset.resetTimeoutId);
  }

  navigator.clipboard
    .writeText(text)
    .then(() => {
      const timeoutId = setTimeout(() => {
        btn.innerHTML = btn.dataset.originalHtml;
        btn.dataset.resetTimeoutId = "";
      }, 935);

      btn.dataset.resetTimeoutId = timeoutId;
    })
    .catch((err) => {
      console.error("Failed to copy text:", err);
    });
});
// Handle locale changes
window.addEventListener("zulti:set-locale", (event) => {
  const { locale } = event.detail;

  // Set the locale cookie
  // Cookie expires in 1 year
  const expirationDate = new Date();
  expirationDate.setFullYear(expirationDate.getFullYear() + 1);

  document.cookie = `selectedLocale=${locale}; expires=${expirationDate.toUTCString()}; path=/; SameSite=Lax`;

  // Reload the page to apply the new locale
  window.location.reload();
});
window.addEventListener("phx:analytics:push", (event) => {
  if (window.dataLayer) {
    dataLayer.push(event.detail);
  }
});

// connect if there are any LiveViews on the page
liveSocket.connect();

// The maintenance page is deliberately not a LiveView (see ZultiWeb.MaintenanceController).
const maintenancePage = document.getElementById("maintenance-page");
if (maintenancePage) {
  import("./maintenance")
    .then(({ mount }) => mount(maintenancePage))
    .catch((error) => {
      if (!isChunkLoadError(error)) sentryCaptureException(error);
    });
}

// A LongPoll response that never arrives loses its diffs (the server has already
// flushed them), so later diffs reference statics the client never received and
// rendering throws. Reconnecting rejoins every LiveView with a full render.
let liveViewResyncing = false;
window.addEventListener("error", ({ error }) => {
  if (liveViewResyncing || !error?.stack?.includes("dynamicsToBuffer")) return;

  liveViewResyncing = true;
  liveSocket.disconnect(() => {
    liveViewResyncing = false;
    liveSocket.connect();
  });
});

// expose liveSocket on window for web console debug logs and latency simulation:
// >> liveSocket.enableDebug()
// >> liveSocket.enableLatencySim(1000)  // enabled for duration of browser session
// >> liveSocket.disableLatencySim()
window.liveSocket = liveSocket;
